import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import {
  ArrowLeft, ArrowRightCircle, Calendar, DollarSign, Edit, Loader2, FileText,
  AlertCircle, Package, Plus, Trash2, User, Building2, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { DateCell } from "@/components/DateCell";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface CpoLine {
  id: string;
  lineNumber: number;
  customerSku: string | null;
  customerDescription: string | null;
  customerUom: string | null;
  quantity: string;
  unitPrice: string | null;
  extendedAmount: string | null;
  resolvedProductId: string | null;
  resolutionConfidence: string;
  lineStatus: string;
  notes: string | null;
  product?: { id: string; name: string; sku: string | null };
}

interface Cpo {
  id: string;
  customerPoNumber: string | null;
  customerPoDate: string | null;
  source: string;
  status: string;
  accountId: string | null;
  account?: { id: string; name: string };
  accountName?: string;
  convertedOrderId?: string | null;
  customerBuyerName: string | null;
  customerBuyerEmail: string | null;
  customerBuyerPhone: string | null;
  customerDepartment: string | null;
  customerPaymentTerms: string | null;
  customerInstructions: string | null;
  poSubtotal: string | null;
  poTaxAmount: string | null;
  poTotalAmount: string | null;
  currencyCode: string | null;
  requestedDelivery: string | null;
  priority: string;
  notes: string | null;
  internalNotes: string | null;
  createdAt: string;
  lines?: CpoLine[];
}

const STATUSES = [
  { value: "RECEIVED", label: "Received" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "PENDING_RESOLUTION", label: "Pending Resolution" },
  { value: "ACKNOWLEDGED", label: "Acknowledged" },
  { value: "PARTIALLY_CONVERTED", label: "Partially Converted" },
  { value: "CONVERTED", label: "Converted" },
  { value: "IN_FULFILLMENT", label: "In Fulfillment" },
  { value: "FULFILLED", label: "Fulfilled" },
  { value: "CLOSED", label: "Closed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXCEPTION", label: "Exception" },
];

const PRIORITIES = [
  { value: "URGENT", label: "Urgent" },
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
];

function formatStatusLabel(s: string) {
  return s.replace(/_/g, " ").split(" ").map(w => w[0] + w.slice(1).toLowerCase()).join(" ");
}

function statusDotColor(status: string) {
  switch (status) {
    case "RECEIVED": return "bg-blue-500";
    case "UNDER_REVIEW": return "bg-yellow-500";
    case "PENDING_RESOLUTION": return "bg-orange-500";
    case "ACKNOWLEDGED": return "bg-indigo-500";
    case "PARTIALLY_CONVERTED": return "bg-indigo-400";
    case "CONVERTED": return "bg-green-500";
    case "IN_FULFILLMENT": return "bg-purple-500";
    case "FULFILLED": return "bg-emerald-500";
    case "CLOSED": return "bg-gray-500";
    case "REJECTED": return "bg-red-500";
    case "CANCELLED": return "bg-gray-400";
    case "EXCEPTION": return "bg-red-600";
    default: return "bg-gray-400";
  }
}

function priorityIndicator(priority: string) {
  switch (priority) {
    case "URGENT":
      return <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-semibold text-xs">● URGENT</span>;
    case "HIGH":
      return <span className="inline-flex items-center gap-1 text-orange-600 dark:text-orange-400 font-medium text-xs">● HIGH</span>;
    case "NORMAL":
      return <span className="text-muted-foreground text-xs">Normal</span>;
    case "LOW":
      return <span className="text-muted-foreground/60 text-xs">Low</span>;
    default:
      return <span className="text-muted-foreground text-xs">{priority}</span>;
  }
}

/**
 * Decide whether the CPO can be converted to an order and render the
 * appropriate button state. Mirrors the worker's strict-mode validation in
 * POST /api/customer-pos/:id/convert so the user gets immediate feedback
 * rather than a round-trip error.
 */
function renderConvertButton(opts: {
  cpo: Cpo;
  isPending: boolean;
  onConvert: () => void;
  onOpenOrder: (orderId: string) => void;
}) {
  const { cpo, isPending, onConvert, onOpenOrder } = opts;

  // Already converted: show a "View Order" affordance instead.
  if (cpo.status === "CONVERTED") {
    if (cpo.convertedOrderId) {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onOpenOrder(cpo.convertedOrderId!)}
          data-testid="button-open-converted-order"
        >
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-green-600" /> View Order
        </Button>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 px-2 py-1 rounded bg-green-50 dark:bg-green-950/40"
        data-testid="badge-already-converted"
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> Converted
      </span>
    );
  }

  const lines = cpo.lines || [];
  const blockers: string[] = [];
  if (!cpo.accountId) blockers.push("CPO has no linked account");
  if (lines.length === 0) blockers.push("CPO has no line items");
  const unresolved = lines.filter((l) => !l.resolvedProductId);
  if (unresolved.length > 0) {
    blockers.push(
      `${unresolved.length} line${unresolved.length === 1 ? "" : "s"} not yet resolved to a product`,
    );
  }

  const disabled = blockers.length > 0 || isPending;
  const tooltip = blockers.length > 0
    ? `Can't convert: ${blockers.join("; ")}`
    : "Create a sales order from this customer PO";

  return (
    <Button
      variant="default"
      size="sm"
      onClick={onConvert}
      disabled={disabled}
      title={tooltip}
      data-testid="button-convert-to-order"
    >
      {isPending
        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        : <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" />}
      Convert to Order
    </Button>
  );
}

export default function CustomerPoDetailPage() {
  const { sessionId } = useAuth();
  const [, params] = useRoute("/customer-pos/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const id = params?.id;

  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("RECEIVED");
  const [editPriority, setEditPriority] = useState("NORMAL");
  const [editNotes, setEditNotes] = useState("");
  const [editInternalNotes, setEditInternalNotes] = useState("");

  const [addLineOpen, setAddLineOpen] = useState(false);
  const [lineSku, setLineSku] = useState("");
  const [lineDescription, setLineDescription] = useState("");
  const [lineUom, setLineUom] = useState("each");
  const [lineQty, setLineQty] = useState("1");
  const [linePrice, setLinePrice] = useState("0.00");

  const { data: cpo, isLoading, error } = useQuery<Cpo>({
    queryKey: ["/api/customer-pos", id],
    queryFn: () => api.get<Cpo>(`/customer-pos/${id}`, sessionId),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      api.patch<Cpo>(`/customer-pos/${id}`, payload, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customer-pos", id] });
      queryClient.invalidateQueries({ queryKey: ["customer-pos"] });
      toast({ title: "Customer PO updated" });
      setEditOpen(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addLineMutation = useMutation({
    mutationFn: async () => {
      const qty = parseFloat(lineQty || "0");
      const price = parseFloat(linePrice || "0");
      return api.post(`/customer-pos/${id}/lines`, {
        customerSku: lineSku || null,
        customerDescription: lineDescription,
        customerUom: lineUom,
        quantity: String(qty),
        unitPrice: String(price),
        extendedAmount: String((qty * price).toFixed(2)),
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customer-pos", id] });
      toast({ title: "Line added" });
      setAddLineOpen(false);
      setLineSku("");
      setLineDescription("");
      setLineUom("each");
      setLineQty("1");
      setLinePrice("0.00");
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteLineMutation = useMutation({
    mutationFn: (lineId: string) =>
      api.delete(`/customer-pos/${id}/lines/${lineId}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customer-pos", id] });
      toast({ title: "Line deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const convertMutation = useMutation({
    mutationFn: () =>
      api.post<{ orderId: string; orderNumber: string }>(`/customer-pos/${id}/convert`, {}, sessionId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["/api/customer-pos", id] });
      queryClient.invalidateQueries({ queryKey: ["customer-pos"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast({
        title: "Converted to sales order",
        description: `Created order ${res.orderNumber}.`,
      });
      setLocation(`/orders/${res.orderId}`);
    },
    onError: (e: Error) =>
      toast({ title: "Conversion blocked", description: e.message, variant: "destructive" }),
  });

  const handleEditOpen = () => {
    if (cpo) {
      setEditStatus(cpo.status || "RECEIVED");
      setEditPriority(cpo.priority || "NORMAL");
      setEditNotes(cpo.notes || "");
      setEditInternalNotes(cpo.internalNotes || "");
    }
    setEditOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      status: editStatus,
      priority: editPriority,
      notes: editNotes || null,
      internalNotes: editInternalNotes || null,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !cpo) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Customer PO not found</p>
        <Button variant="outline" onClick={() => setLocation("/customer-pos")}>
          Back to Customer POs
        </Button>
      </div>
    );
  }

  const lines = cpo.lines || [];
  const totalAmount = Number(cpo.poTotalAmount || 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/customer-pos")}
                className="text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-back-to-customer-pos"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight" data-testid="text-cpo-number">
                {cpo.customerPoNumber || "Untitled PO"}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {cpo.account && cpo.accountId && (
                <button
                  onClick={() => setLocation(`/accounts/${cpo.accountId}`)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                  data-testid="link-account"
                >
                  {cpo.account.name}
                </button>
              )}
              {cpo.account && <span className="text-muted-foreground font-normal">/</span>}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span data-testid="text-cpo-status">{formatStatusLabel(cpo.status)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {renderConvertButton({
              cpo,
              isPending: convertMutation.isPending,
              onConvert: () => convertMutation.mutate(),
              onOpenOrder: (orderId) => setLocation(`/orders/${orderId}`),
            })}
            <Button variant="outline" size="sm" onClick={handleEditOpen} data-testid="button-edit-cpo">
              <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        </div>

        {/* Mini-cards strip */}
        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto" data-testid="mini-cards-strip">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor(cpo.status))} />
              <span className="text-sm font-medium">{formatStatusLabel(cpo.status)}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Priority</span>
            {priorityIndicator(cpo.priority)}
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Total</span>
              <p className="text-sm font-medium" data-testid="text-total">
                ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Lines</span>
              <p className="text-sm font-medium" data-testid="text-line-count">{lines.length}</p>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">PO Date</span>
              <p className="text-sm font-medium" data-testid="text-po-date">
                <DateCell value={cpo.customerPoDate || cpo.createdAt} fallback="—" />
              </p>
            </div>
          </div>
          {cpo.requestedDelivery && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground">Requested Delivery</span>
                  <p className="text-sm font-medium" data-testid="text-requested-delivery">
                    <DateCell value={cpo.requestedDelivery} fallback="—" />
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Customer + Buyer cards */}
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border-l-[3px] border-blue-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-500" />
              Customer
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Account</span>
              <span className="text-sm font-medium">{cpo.account?.name || cpo.accountName || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Source</span>
              <span className="text-sm font-medium">{cpo.source}</span>
            </div>
            {cpo.customerPaymentTerms && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Payment Terms</span>
                <span className="text-sm font-medium">{cpo.customerPaymentTerms}</span>
              </div>
            )}
            {cpo.currencyCode && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Currency</span>
                <span className="text-sm font-medium">{cpo.currencyCode}</span>
              </div>
            )}
            {cpo.customerInstructions && (
              <div>
                <span className="text-xs text-muted-foreground">Customer Instructions</span>
                <p className="text-sm whitespace-pre-wrap">{cpo.customerInstructions}</p>
              </div>
            )}
          </div>
        </div>

        <div className="border-l-[3px] border-violet-500 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <User className="h-4 w-4 text-violet-500" />
              Buyer Contact
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {cpo.customerBuyerName ? (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="text-sm font-medium">{cpo.customerBuyerName}</span>
              </div>
            ) : null}
            {cpo.customerBuyerEmail && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Email</span>
                <a href={`mailto:${cpo.customerBuyerEmail}`} className="text-sm font-medium text-blue-600 hover:underline">
                  {cpo.customerBuyerEmail}
                </a>
              </div>
            )}
            {cpo.customerBuyerPhone && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Phone</span>
                <span className="text-sm font-medium">{cpo.customerBuyerPhone}</span>
              </div>
            )}
            {cpo.customerDepartment && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Department</span>
                <span className="text-sm font-medium">{cpo.customerDepartment}</span>
              </div>
            )}
            {!cpo.customerBuyerName && !cpo.customerBuyerEmail && !cpo.customerBuyerPhone && (
              <p className="text-sm text-muted-foreground">No buyer contact captured.</p>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {(cpo.notes || cpo.internalNotes) && (
        <div className="border-l-[3px] border-gray-400 bg-card rounded-r-lg shadow-sm">
          <div className="px-4 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Notes
            </h2>
          </div>
          <div className="px-4 pb-4 space-y-3">
            {cpo.notes && (
              <div>
                <span className="text-xs text-muted-foreground">External Notes</span>
                <p className="text-sm whitespace-pre-wrap">{cpo.notes}</p>
              </div>
            )}
            {cpo.internalNotes && (
              <div>
                <span className="text-xs text-muted-foreground">Internal Notes</span>
                <p className="text-sm whitespace-pre-wrap">{cpo.internalNotes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Line Items */}
      <div className="border-l-[3px] border-emerald-500 bg-card rounded-r-lg shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4 text-emerald-500" />
            Line Items
          </h2>
          <Button size="sm" variant="outline" onClick={() => setAddLineOpen(true)} data-testid="button-add-line">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Line
          </Button>
        </div>
        <div className="px-4 pb-4">
          {lines.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Extended</TableHead>
                    <TableHead>Resolution</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id} data-testid={`row-cpo-line-${line.lineNumber}`}>
                      <TableCell className="font-medium">{line.lineNumber}</TableCell>
                      <TableCell className="text-xs">{line.customerSku || line.product?.sku || "—"}</TableCell>
                      <TableCell>{line.product?.name || line.customerDescription || "—"}</TableCell>
                      <TableCell className="text-xs">{line.customerUom || "—"}</TableCell>
                      <TableCell className="text-right">{Number(line.quantity || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right">${Number(line.unitPrice || 0).toFixed(4)}</TableCell>
                      <TableCell className="text-right font-medium">
                        ${Number(line.extendedAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <span className={cn(
                          "text-[10px] px-2 py-0.5 rounded font-medium",
                          line.resolutionConfidence === "EXACT" || line.resolutionConfidence === "MANUAL_OVERRIDE"
                            ? "bg-green-100 text-green-800"
                            : line.resolutionConfidence === "LIKELY"
                              ? "bg-blue-100 text-blue-800"
                              : line.resolutionConfidence === "AMBIGUOUS"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-700"
                        )}>
                          {line.resolutionConfidence}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteLineMutation.mutate(line.id)}
                          disabled={deleteLineMutation.isPending}
                          data-testid={`button-delete-line-${line.lineNumber}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
              <Package className="h-10 w-10 mb-2 opacity-50" />
              <p>No line items</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Customer PO</DialogTitle>
            <DialogDescription>Update status, priority and notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Status *</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="select-edit-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Priority</label>
              <Select value={editPriority} onValueChange={setEditPriority}>
                <SelectTrigger data-testid="select-edit-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                placeholder="External notes..."
                className="min-h-[80px]"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                data-testid="textarea-edit-notes"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Internal Notes</label>
              <Textarea
                placeholder="Internal notes..."
                className="min-h-[80px]"
                value={editInternalNotes}
                onChange={(e) => setEditInternalNotes(e.target.value)}
                data-testid="textarea-edit-internal-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-cpo">
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Line Dialog */}
      <Dialog open={addLineOpen} onOpenChange={setAddLineOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
            <DialogDescription>Manually add a line to this customer PO.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Customer SKU</label>
                <Input value={lineSku} onChange={(e) => setLineSku(e.target.value)} placeholder="ABC-123" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">UOM</label>
                <Input value={lineUom} onChange={(e) => setLineUom(e.target.value)} placeholder="each" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description *</label>
              <Input value={lineDescription} onChange={(e) => setLineDescription(e.target.value)} placeholder="Line description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Quantity *</label>
                <Input
                  type="number"
                  step="0.001"
                  value={lineQty}
                  onChange={(e) => setLineQty(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Unit Price *</label>
                <Input
                  type="number"
                  step="0.0001"
                  value={linePrice}
                  onChange={(e) => setLinePrice(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLineOpen(false)}>Cancel</Button>
            <Button
              onClick={() => addLineMutation.mutate()}
              disabled={addLineMutation.isPending || !lineDescription.trim()}
              data-testid="button-save-line"
            >
              {addLineMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}